import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Container } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { Formik, FormikValues, yupToFormErrors } from 'formik';
import { merge, isEqual, find } from 'lodash';

import history from 'browserHistory';
import { useModal } from 'modules/shared/hooks';
import FlightTop from 'modules/shared/FlightTop';
import FlightDetails from 'modules/shared/FlightDetails';
import FormikPatchTouched from 'modules/shared/Forms/FormikPatchTouched';
import FormikFillFields from 'modules/shared/Forms/FormikFillFields';
import DepartureData from 'modules/shared/DepartureData';
import UnloadingData from 'modules/shared/UnloadingData';
import DepartureComment from 'modules/shared/DepartureComment';
import ModalWrapper from 'modules/shared/ModalWrapper';
import YesNoModal from 'modules/shared/YesNoModal';
import { peripheralsSelector } from 'modules/common/Header/selectors';
import { usePrepareValues } from 'modules/common/CommonTrip/hooks/usePrepareValues';
import { cleanTripFields } from 'modules/common/FillTrip';
import { useValidationSchema } from 'modules/common/CommonTrip/hooks/useValidationSchema';
import ErrorMsg from 'modules/shared/ErrorMsg';
import Loader from 'modules/kiosk/components/Loader';
import {
  cleanError,
  cleanWarning,
  getTripRequest,
  updateTripRequest,
  updateTripStatus,
} from 'modules/common/CommonTrip/actions';
import { deleteTab } from 'modules/common/Tabs/actions';
import { commonTripSelector } from 'modules/common/CommonTrip/selectors';
import { FormikCheckValid } from 'modules/common/CommonTrip/hooks/FormikCheckValid';
import classNames from 'classnames';
import { RootState } from 'rootReducer';
import { tripStatuses, tripIntentionStatuses } from '../../../../entities/Trip/constants';
import { getDrIdReguest } from './services/actions';
import { loadingSelector } from './services/selectors';
import { logoutSelector } from '../../../common/LogoutBtn/selectors';
import styles from './TripContainer.module.scss';
import { bookingModalSelector } from '../../../common/BookingModal/selector';

type Params = {
  tripId: string;
};

export const TripContainer: React.FC = () => {
  const dispatch = useDispatch();
  const [allowedExitRealTolerance, setAllowedExitRealTolerance] = useState<number>(0);
  const [allowedExitTheoreticalTolerance, setAllowedExitTheoreticalTolerance] = useState<number>(0);
  const formRef = useRef();
  const { tripId = '' } = useParams<Params>();

  const { isShow, openModal, closeModal } = useModal();
  const { isShow: isShowYN, openModal: openYN, closeModal: closeYN } = useModal();
  const { isShow: isShowErr, openModal: openErrToast, closeModal: closeErrToast } = useModal();
  const { isShow: isShowWeight, openModal: openWeight, closeModal: closeWeight } = useModal();

  const [tripDataReq, setTripDataReq] = useState<any>();
  const [{ isWrongTolerance }, setCallAdminTolerance] = useState<{
    isWrongTolerance: boolean;
    tolerance: number;
  }>({ isWrongTolerance: false, tolerance: 0 });

  const { loadingTrip, errorTrip, trip, warning, warningText } = useSelector(commonTripSelector);
  const { loadingModal } = useSelector(bookingModalSelector);

  const role = useSelector((state: RootState) => state.authModuleReducer.authReducer.role);

  const operatingProcedure = useMemo(() => {
    if (role?.branches && role.branches.length !== 0) {
      return role.branches.find((branch) => branch?.id === JSON.parse(localStorage.getItem('branchId') || 'null')?.id)
        ?.operatingProcedure;
    }

    return null;
  }, [role]);

  const { id, status } = trip;
  const statusId = status?.id || 1;

  const loadingStatus = useSelector(loadingSelector);
  const peripherals = useSelector(peripheralsSelector);
  const { branches, branchTitle } = useSelector(logoutSelector);
  const filtered = find(branches, (branch) => branch?.id === branchTitle?.id);

  const initValues = usePrepareValues(id, trip);

  useEffect(() => {
    const realTolerance =
      peripherals?.find((per) => isEqual(per.type, 'Scales') && isEqual(per.processName, 'Exit'))
        ?.allowedExitTolerance || 0;
    const theoreticalTolerance =
      peripherals?.find((per) => isEqual(per.type, 'Scales') && isEqual(per.processName, 'Exit'))
        ?.allowedExitTheoreticalTolerance || 0;

    setAllowedExitRealTolerance(realTolerance);
    setAllowedExitTheoreticalTolerance(theoreticalTolerance);
  }, [peripherals, id]);

  // @ts-ignore
  const intentionForm = formRef.current?.values?.tripIntention;

  const { tripIntention } = initValues;
  const loading = +intentionForm === tripIntentionStatuses.loading;
  const useVisitorDoc: boolean = branchTitle?.useVisitorDoc ?? true;
  const useTransportDoc: boolean = branchTitle?.useTransportDoc ?? true;
  const validationSchema = useValidationSchema(
    'security',
    statusId,
    loading,
    operatingProcedure ?? '',
    useVisitorDoc,
    useTransportDoc,
  );
  // @ts-ignore
  const allowWeight = trip?.allowedWeight || 0;

  const restStatus =
    statusId === tripStatuses.parkingInLine ||
    statusId === tripStatuses.waitingOrder ||
    statusId === tripStatuses.waiting ||
    statusId === tripStatuses.downloading ||
    statusId === tripStatuses.parking ||
    statusId === tripStatuses.onPause;

  // @ts-ignore
  const orders = formRef.current?.values?.orders;
  const isCallAdmin =
    (isWrongTolerance && loading && orders && orders.length) || (isWrongTolerance && loading && status?.id === 2);

  let btnText = 'Зареєструвати виїзд';
  let modalTitle = '';

  if (statusId === tripStatuses.create) {
    btnText = "Зареєструвати в'їзд";
  }
  if (
    (statusId === tripStatuses.create ||
      statusId === tripStatuses.parkingInLine ||
      statusId === tripStatuses.waiting) &&
    operatingProcedure === 'Порядок 2'
  ) {
    btnText = 'Поставити в чергу';
  }
  if (isCallAdmin) {
    btnText = 'Викликати завсклада';
    modalTitle = `Завсклада викликано для рейсу ${tripId}`;
  } else {
    modalTitle = `Для рейсу ${tripId} зареєстровано ${statusId === tripStatuses.create ? "в'їзд" : 'виїзд'}`;
  }
  if (statusId === tripStatuses.waitingAdmin) {
    btnText = 'Дозволити виїзд';
    modalTitle = `Для рейсу ${tripId} дозволено виїзд`;
  }

  if (restStatus && !isCallAdmin && (!orders || !orders.length)) {
    btnText = 'Виїзд без завантаження';
    modalTitle = `Для рейсу ${tripId} дозволено виїзд`;
  }

  const faceIdUrl = peripherals?.find((p) => p.type === 'FaceID')?.peripheralUrl;

  const closeTabHandler = () => {
    const timer = setTimeout(() => {
      dispatch(deleteTab(`trip_${tripId}`));
      closeModal();
      if (tripIntention === 1) {
        return history.push('/security');
      }
      return history.push('/security/unload');
    }, 3000);

    clearTimeout(timer);
    dispatch(deleteTab(`trip_${tripId}`));
    closeModal();
    if (tripIntention === 1) {
      return history.push('/security');
    }
    return history.push('/security/unload');
  };

  const onWeighing = () => {
    dispatch(updateTripStatus(trip));
    closeWeight();
    dispatch(deleteTab(`trip_${tripId}`));
    if (tripIntention === 1) {
      return history.push('/security');
    }
    return history.push('/security/unload');
  };

  const regDepartureHandler = () => {
    closeYN();
    dispatch(updateTripRequest(tripDataReq, false, status?.id));
    dispatch(cleanTripFields());
    openModal();

    return closeTabHandler();
  };

  const onFaceIdDriver = (type: string, tripValues: any) => {
    if (faceIdUrl) dispatch(getDrIdReguest(faceIdUrl, type, tripValues));
  };

  const isBlockPhoneVerification = () => {
    if (statusId !== 1) return false;
    if (!filtered || !filtered.sendPhoneVerification) return false;

    // @ts-ignore
    const driver = formRef.current?.values;
    if (driver?.driver && driver?.driver.firstName?.length && driver?.isDriverPhoneNumberValid === false) return true;

    // @ts-ignore
    const forwarder = formRef.current?.values;
    if (
      forwarder?.forwarder &&
      forwarder?.forwarder.firstName?.length &&
      forwarder?.isForwarderPhoneNumberValid === false
    )
      return true;

    return false;
  };

  useEffect(() => {
    if (tripId && !id) {
      dispatch(getTripRequest(tripId));
    }
  }, [id]);

  useEffect(() => {
    if (errorTrip) {
      return openErrToast();
    }
    closeErrToast();
  }, [errorTrip]);

  const validate = (v: any) => {
    try {
      validationSchema.validateSync(v, { abortEarly: false, context: { values: v } });
      return {};
    } catch (error) {
      // @ts-igonre
      console.log((error as any)?.errors);
      return yupToFormErrors<FormikValues>(error);
    }
  };

  return (
    <Container>
      {(loadingStatus || loadingTrip || loadingModal || (!id && !errorTrip)) && <Loader />}
      {!loadingStatus && !loadingTrip && id && statusId !== 99 ? (
        <Formik
          // @ts-ignore
          innerRef={formRef}
          initialValues={initValues}
          // validationSchema={validationSchema}
          validate={validate}
          enableReinitialize
          validateOnMount
          onSubmit={(values) => {
            const tripData = merge({ id }, values);
            // @ts-ignore
            let newStatusId = tripStatuses.left;
            if (statusId === tripStatuses.create) {
              newStatusId = String(values.tripIntention) === '1' ? tripStatuses.parkingInLine : tripStatuses.unloading;
            }
            if (isCallAdmin && statusId !== tripStatuses.waitingAdmin) {
              newStatusId = tripStatuses.waitingAdmin;
              tripData.callDepartmentManagerTime = new Date().toUTCString();
              tripData.allowWithTolerance = false;
            }
            if (statusId === tripStatuses.waitingAdmin) {
              newStatusId = tripStatuses.waitingDeparture;
            }
            if (statusId === tripStatuses.parkingInLine) {
              newStatusId = tripStatuses.left;
            }
            if (statusId !== tripStatuses.create && !isCallAdmin) {
              tripData.departureDate = new Date().toJSON();
            }
            tripData.statusId = newStatusId;

            setTripDataReq(tripData);
            openYN();
          }}
        >
          {({ values, handleChange, handleSubmit, setFieldValue, isValid, errors }) => (
            <form onSubmit={handleSubmit} className="mt-4">
              <FormikPatchTouched />
              <FormikFillFields />
              <FormikCheckValid data={values} />
              <FlightTop
                status={status}
                tripId={id}
                handleChange={handleChange}
                tripIntention={values.tripIntention}
                isSecurity
                hideTripIntention
              />
              <FlightDetails
                mode="security"
                statusId={statusId}
                tripType="driver"
                onFaceID={onFaceIdDriver}
                handleChange={handleChange}
                operatingProcedure={operatingProcedure ?? ''}
              />
              <FlightDetails
                mode="security"
                statusId={statusId}
                tripType="vehicle"
                handleChange={handleChange}
                operatingProcedure={operatingProcedure ?? ''}
              />
              <FlightDetails
                mode="security"
                statusId={statusId}
                tripType="forwarder"
                onFaceID={onFaceIdDriver}
                handleChange={handleChange}
                operatingProcedure={operatingProcedure ?? ''}
              />
              {(statusId > tripStatuses.downloading || statusId > tripStatuses.create) && tripIntention === 1 && (
                <DepartureData
                  mode="security"
                  statusId={statusId}
                  values={values}
                  errors={errors}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  setCallAdminTolerance={setCallAdminTolerance}
                  allowWeight={allowWeight}
                  allowedExitRealTolerance={allowedExitRealTolerance}
                  allowedExitTheoreticalTolerance={allowedExitTheoreticalTolerance}
                  restStatus={restStatus}
                  operatingProcedure={operatingProcedure ?? ''}
                />
              )}
              {statusId !== tripStatuses.create && tripIntention !== 1 && (
                <UnloadingData mode="security" values={values} handleChange={handleChange} errors={errors} />
              )}
              <DepartureComment mode="security" statusId={statusId} handleChange={handleChange} />
              {statusId !== 3 && statusId !== 4 && statusId !== 5 ? (
                <div className="d-flex justify-content-end mt-4 mb-5">
                  {values.statusId === 6 ? (
                    <Button
                      type="button"
                      className={classNames(styles.btn, 'px-3 text-uppercase mr-4')}
                      variant="danger"
                      onClick={openWeight}
                    >
                      <h5 className="my-2 text-white mx-5">Рейс на виписку</h5>
                    </Button>
                  ) : null}
                  {statusId === 2 || statusId === tripStatuses.waitingOrder ? (
                    <Button
                      className={classNames(styles.btn, 'px-3 text-uppercase')}
                      variant="danger"
                      onClick={() => {
                        const updateTripData = merge(
                          { id },
                          {
                            ...values,
                            isNotLoaded: true,
                            statusId: 99,
                          },
                        );

                        dispatch(updateTripRequest(updateTripData, false, status?.id));

                        dispatch(cleanTripFields());
                        return closeTabHandler();
                      }}
                      disabled={!isValid}
                    >
                      <h5 className="my-1 text-white mx-4">Виїзд без завантаження</h5>
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      className={classNames(styles.btn, 'px-3 text-uppercase')}
                      variant="danger"
                      disabled={
                        !isValid ||
                        isBlockPhoneVerification() ||
                        (operatingProcedure === 'Порядок 2' && (statusId === 1 || statusId === 2 || statusId === 3))
                      }
                    >
                      <h5 className="my-1 text-white mx-4">{btnText}</h5>
                    </Button>
                  )}
                </div>
              ) : null}
            </form>
          )}
        </Formik>
      ) : null}

      {statusId === 99 ? (
        <h3 className="m-5 text-center">
          Порушення доступу до деталей рейсу.{' '}
          <button
            className={styles.button}
            onClick={() => {
              dispatch(deleteTab(`trip_${tripId}`));
              if (tripIntention === 1) {
                history.push('/security');
              } else {
                history.push('/security/unload');
              }
            }}
            type="button"
          >
            <h3 className={styles.text}>На головну</h3>
          </button>
        </h3>
      ) : null}

      <ModalWrapper show={isShowWeight} onHide={closeWeight}>
        <div>
          <h6 className="text-center">Рейс буде повернуто в попередній статус з можливістю редагування на виписці.</h6>
          <h6 className="text-center">Продовжити?</h6>
          <div className="d-flex justify-content-center mt-5">
            <Button className="px-5 mr-5" variant="outline-secondary" onClick={closeWeight}>
              Ні
            </Button>
            <Button className="px-5" variant="outline-danger" onClick={onWeighing}>
              Так
            </Button>
          </div>
        </div>
      </ModalWrapper>
      <ModalWrapper show={isShow} onHide={closeTabHandler}>
        <div>
          <h6 className="text-center">{modalTitle}</h6>
          <div className="d-flex justify-content-center mt-3">
            <Button className="px-5" variant="outline-secondary" onClick={closeTabHandler}>
              Ok
            </Button>
          </div>
        </div>
      </ModalWrapper>

      <ModalWrapper show={warning} onHide={() => dispatch(cleanWarning())}>
        <div className="d-flex justify-content-center mb-3">
          <h3>Увага</h3>
        </div>
        <div>
          {warningText?.map((text: any) => (
            <h6 className="text-center mb-3">{text}</h6>
          ))}
          <div className="d-flex justify-content-center mt-4">
            <Button className="px-5" variant="outline-secondary" onClick={() => dispatch(cleanWarning())}>
              Ok
            </Button>
          </div>
        </div>
      </ModalWrapper>

      <YesNoModal
        isShow={isShowYN}
        title={`${btnText} для рейсу ${id}`}
        acceptHandler={regDepartureHandler}
        closeModal={closeYN}
      />
      <ErrorMsg
        isShow={isShowErr}
        closeHandler={() => {
          closeErrToast();
          dispatch(cleanError());
        }}
        error={errorTrip?.data?.Errors[0]?.Message || errorTrip?.message || 'Виникла помилка'}
      />
    </Container>
  );
};
